"use client";

import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./Card";
import {
	XAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	LineChart,
	Line,
} from "recharts";
import { add } from "date-fns";
import SalesReportSkeleton, {
	SalesReportChartSkeleton,
} from "./skeletons/SalesReportSkeleton";
import OptionsSelector from "./OptionsSelector";
import OrgApi from "../../../../API/OrgApi";
import { UserContext } from "../../../../App";

const orgToColorMap = {
	2: "#00AE48",
	6: "#FCAE00",
	11: "#00A9F7",
	12: "#FA1F19",
};

const getRandomColor = () => {
	const letters = "0123456789ABCDEF";
	let color = "#";
	for (let i = 0; i < 6; i++) {
		color += letters[Math.floor(Math.random() * 16)];
	}
	return color;
};

const rangeOptions = [
	{ label: "12 Months", value: "12_MONTHS" },
	{ label: "3 Months", value: "3_MONTHS" },
	{ label: "30 Days", value: "30_DAYS" },
	{ label: "7 Days", value: "7_DAYS" },
	{ label: "24 Hours", value: "24_HOURS" },
];

const rangeToMinDate = {
	"12_MONTHS": add(new Date(), { years: -1 }),
	"3_MONTHS": add(new Date(), { months: -3 }),
	"30_DAYS": add(new Date(), { days: -30 }),
	"7_DAYS": add(new Date(), { days: -7 }),
	"24_HOURS": add(new Date(), { hours: -24 }),
};

export default function SalesReport({ fiberCompanyId }) {
	const [data, setData] = useState({
		installed: [],
		sales: [],
	});
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState("SALES");
	const [range, setRange] = useState("12_MONTHS");
	const [init, setInit] = useState(false);

	const [allLabels, setAllLabels] = useState([]);
	const [selectLabelIds, setSelectedLabelIds] = useState([]);
	const user = useContext(UserContext);

	useEffect(() => {
		setSelectedLabelIds(allLabels.map((o) => o.id));
	}, [allLabels]);

	useEffect(() => {
		fetchData().finally(() => setInit(true));
	}, [range, fiberCompanyId, user]);

	const fetchData = async () => {
		setLoading(true);
		const minDate = rangeToMinDate[range];

		const res = await OrgApi.getDashboardSalesReportData(
			minDate.toISOString(),
			fiberCompanyId,
			user.id,
		);

		const result = res.data;

		const allLabels = result.allLabels;

		const installedData = result.installed.map(({ date, data }) => {
			const currentIds = data.map(({ id }) => id);

			const filledData = [...data];

			allLabels.forEach(({ id }) => {
				if (!currentIds.includes(id)) {
					filledData.push({ id, number: 0 });
				}
			});
			return {
				date,
				...filledData.reduce(
					(prev, { id, number }) => ({
						...prev,
						[`label-${id}`]: number,
					}),
					{},
				),
			};
		});

		const salesData = result.sales.map(({ date, data }) => {
			const currentOrgIds = data.map(({ id }) => id);

			const filledData = [...data];

			allLabels.forEach(({ id }) => {
				if (!currentOrgIds.includes(id)) {
					filledData.push({ id, number: 0 });
				}
			});
			return {
				date,
				...filledData.reduce(
					(prev, { id, number }) => ({
						...prev,
						[`label-${id}`]: number,
					}),
					{},
				),
			};
		});

		function markRepeatedDates(data) {
			const mainParts = data.map((d) => d.date.split(", ")[0]);

			const repeatedSet = new Set(
				mainParts.filter((date, index, arr) => arr.indexOf(date) !== index),
			);

			return data.map((d) => {
				const mainPart = d.date.split(", ")[0];
				return {
					...d,
					isRepeated: repeatedSet.has(mainPart),
				};
			});
		}

		setAllLabels(
			allLabels.map(({ id, name }) => ({
				id,
				color: orgToColorMap[id] ?? getRandomColor(),
				name,
			})),
		);
		setData({
			installed: markRepeatedDates(installedData),
			sales: markRepeatedDates(salesData),
		});

		setLoading(false);
	};

	const activeData = useMemo(
		() => (activeTab === "INSTALLS" ? data.installed : data.sales),
		[activeTab, data],
	);

	const toggleOrgSelection = (id) => {
		setSelectedLabelIds((prev) =>
			prev.includes(id) ? prev.filter((orgId) => orgId !== id) : [...prev, id],
		);
	};

	console.log({ data });
	console.log({ activeData });

	if (!init) return <SalesReportSkeleton />;

	return (
		<Card className="flex-1 flex flex-col">
			<CardHeader className="flex flex-col gap-4 lg:gap-5">
				<div className="flex flex-col gap-3.5 items-start lg:flex-row lg:items-center lg:gap-3">
					<CardTitle className="text-lg font-semibold ">Sales Report</CardTitle>

					<div className="flex-1 flex flex-row gap-2 w-full justify-between lg:flex-row lg:flex-1 lg:w-auto">
						{/* <button className="px-3.5 py-2.5 font-semibold text-sm border shadow-sm rounded-lg">
							View Leaderboard
						</button> */}

						<OptionsSelector
							options={[
								{ label: "Sales", value: "SALES" },
								{ label: "Installs", value: "INSTALLS" },
							]}
							onSelect={setActiveTab}
							selected={activeTab}
						/>
					</div>
				</div>
				<OptionsSelector
					options={rangeOptions}
					selected={range}
					onSelect={setRange}
				/>

				{!loading && allLabels.length > 0 && (
					<div className="flex flex-col gap-2">
						<div className="self-start flex gap-3 flex-wrap">
							{allLabels.map(({ id, color, name }) => {
								const isSelected = selectLabelIds.includes(id);
								return (
									<div
										key={id}
										className={`flex gap-2 items-center flex-nowrap px-2 py-1 rounded-md cursor-pointer transition-colors ${
											false ? "bg-gray-100" : "hover:bg-gray-50"
										}`}
										onClick={() => toggleOrgSelection(id)}
									>
										<div
											className={`rounded-full w-3 h-3 flex items-center justify-center ${
												isSelected ? "ring-1 ring-gray-300" : ""
											}`}
											style={{ background: color }}
										>
											{isSelected && (
												<div className="w-1.5 h-1.5 rounded-full bg-white" />
											)}
										</div>
										<div className="text-sm text-nowrap">{name}</div>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</CardHeader>
			{loading ? (
				<SalesReportChartSkeleton />
			) : !activeData.length ? (
				<div className="flex-1 flex justify-center items-center p-10">
					<div className="text-sm font-semibold text-gray-400">
						No Data Found
					</div>
				</div>
			) : (
				<CardContent className="flex flex-col h-[400px]">
					<ResponsiveContainer width="100%" height="100%">
						<LineChart
							data={activeData}
							margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
						>
							<CartesianGrid strokeDasharray="3 3" vertical={false} />
							<XAxis
								dataKey="date"
								type="category"
								axisLine={{ stroke: "#E5E7EB" }}
								tickLine={false}
								interval="preserveStartEnd"
								tickFormatter={(date, i) => {
									const mainPart = date.split(",")[0];

									const data = activeData.find((d) => d.date === date);

									return data.isRepeated ? date : mainPart;
								}}
							/>
							<Tooltip />
							{allLabels
								.filter(({ id }) => selectLabelIds.includes(id))
								.map(({ id, color, name }) => (
									<Line
										key={id}
										type="monotone"
										dot={false}
										dataKey={`label-${id}`}
										name={name}
										stroke={color}
										strokeWidth={3}
									/>
								))}
						</LineChart>
					</ResponsiveContainer>
				</CardContent>
			)}
		</Card>
	);
}
